import React from 'react';

/**
 * Standard Stride Switch / Toggle Component
 * Accessible, keyboard-navigable switch with an explicit sliding knob element.
 */
const Toggle = ({
    id,
    name,
    checked = false,
    onChange,
    disabled = false,
    size = 'md',
    className = '',
    label,
    labelPosition = 'right',
    labelClassName = '',
    ariaLabel,
    title,
    ...props
}) => {
    const isChecked = Boolean(checked);

    // Explicit Tailwind classes per size so JIT scanner detects them
    const styles = {
        sm: {
            track: 'h-5 w-9',
            knob: 'h-4 w-4',
            translate: isChecked ? 'translate-x-4' : 'translate-x-0',
            textSize: 'text-xs',
        },
        md: {
            track: 'h-6 w-11',
            knob: 'h-5 w-5',
            translate: isChecked ? 'translate-x-5' : 'translate-x-0',
            textSize: 'text-sm',
        },
        lg: {
            track: 'h-7 w-14',
            knob: 'h-6 w-6',
            translate: isChecked ? 'translate-x-7' : 'translate-x-0',
            textSize: 'text-base',
        },
    };

    const current = styles[size] || styles.md;

    const renderLabel = () => {
        if (!label) return null;
        return (
            <span
                className={`${current.textSize} font-medium ${
                    disabled ? 'text-stride-muted' : 'text-stride-heading'
                } ${labelClassName}`}
            >
                {label}
            </span>
        );
    };

    return (
        <label
            htmlFor={id}
            title={title}
            className={`relative inline-flex items-center gap-2 select-none ${
                disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
            } ${className}`}
        >
            {labelPosition === 'left' && renderLabel()}
            <input
                type="checkbox"
                id={id}
                name={name}
                checked={isChecked}
                onChange={onChange}
                disabled={disabled}
                aria-label={ariaLabel || (typeof label === 'string' ? label : undefined)}
                className="sr-only"
                {...props}
            />
            {/* Switch Track */}
            <div
                className={`relative inline-flex ${current.track} shrink-0 items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 ${
                    isChecked
                        ? disabled
                            ? 'bg-primary/60'
                            : 'bg-primary'
                        : 'bg-slate-300 hover:bg-slate-400/70'
                }`}
            >
                {/* Switch Sliding Knob */}
                <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block ${current.knob} transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${current.translate}`}
                />
            </div>
            {labelPosition === 'right' && renderLabel()}
        </label>
    );
};

export default Toggle;
